import { IconActionChevronDown, IconActionClose } from "@/components/Icons";
import { mcn } from "@/utils/mergeClassNames";
import { Dialog as HeadlessuiDialog, Disclosure } from "@headlessui/react";
import {
  CompetenceAndLevel,
  LearningOutcome,
  Level,
} from "@/server/backend-api/generated";
import {
  LevelTab,
  SkillTab,
} from "@/components/Sections/LearningOutcomeSections";
import { CompetenceBaseEx, CompetenceBaseTreeEx } from "@/server/backend-api";
import {
  CompetenceRelationshipMap,
  getFlatCompetencesWithoutRoot,
} from "@/utils/competenceTree";
import { Assert } from "@/utils/assert";
import Link from "../Link";
import { buildCompetenceUrl } from "@/utils/url";

type Props = {
  open: boolean;
  onClose: () => void;
  outcome: LearningOutcome;
  tree: CompetenceBaseTreeEx;
};

export function LearningOutcomeModal(props: Props) {
  const { onClose, open, outcome, tree } = props;
  const relationshipMap = new CompetenceRelationshipMap(tree);
  const flatCompetences = getFlatCompetencesWithoutRoot(tree);
  return (
    <HeadlessuiDialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 h-full w-full bg-white bg-opacity-30" />
      <div className="fixed inset-0 h-full w-full">
        <div className="flex justify-center items-center min-h-full">
          <div className="p-md w-full max-w-[1000px] relative">
            <HeadlessuiDialog.Panel
              className={mcn(
                "shadow-elevation-3",
                "rounded-2 p-xl tablet:p-2xl ",
                "bg-blue text-white styled-scrollbar max-h-[calc(100vh_-_32px)] tablet-sm:max-h-[85vh] overflow-y-scroll"
              )}
            >
              <button
                className={mcn(
                  "absolute right-6 top-8",
                  "hover:text-yellow-100",
                  "focus-visible:default-focus",
                  "rounded-full p-2xs"
                )}
                onClick={props.onClose}
              >
                <IconActionClose fontSize={24} />
              </button>

              <HeadlessuiDialog.Title className="text-style-headline3">
                {outcome.name}
              </HeadlessuiDialog.Title>
              <HeadlessuiDialog.Description className="text-style-body2 mt-md">
                {outcome.description}
              </HeadlessuiDialog.Description>
              {outcome.competenceAndLevels?.map((competenceAndLevel) => {
                Assert.notNull(competenceAndLevel.competenceId);
                const chain = relationshipMap
                  .getRelationshipChain(competenceAndLevel.competenceId)
                  .reverse()
                  .map((id) => {
                    const c = flatCompetences.find((c) => c.id === id);
                    Assert.notNull(c);
                    return c;
                  });
                return (
                  <div
                    key={competenceAndLevel.competenceId}
                    className={"flex flex-col mt-[58px]"}
                  >
                    <CompetenceHeaderAndContent
                      competenceAndLevel={competenceAndLevel}
                      competenceRelationshipChain={chain}
                      index={0}
                    />
                  </div>
                );
              })}
              <Disclosure>
                {({ open }) => (
                  <div className="flex flex-col border-t border-b border-light-blue mt-xl text-style-body1">
                    <Disclosure.Button>
                      <div className="flex py-md justify-between gap-x-lg">
                        <p>
                          What’s the difference between explorer, expert and
                          pioneer?
                        </p>
                        <IconActionChevronDown
                          className={mcn("h-lg w-lg", open && "rotate-180")}
                        />
                      </div>
                    </Disclosure.Button>

                    {open && (
                      <div className="flex flex-col pb-md">
                        <span className="text-style-subtitle1">Explorer</span>
                        <p className="text-style-body2">
                          The explorer level describes one with a basic
                          understanding and awareness of relevant concepts in
                          the topic area. They have the basic skills required to
                          investigate the topic. And they have the interest and
                          curiosity for exploring the topic.
                        </p>
                        <span className="text-style-subtitle1 mt-md">
                          Expert
                        </span>
                        <p className="text-style-body2">
                          The expert level describes one with a comprehensive
                          understanding of concepts, structures, and mechanisms
                          in the topic area. They have a wide range of skills
                          required operate within relevant topic activities. And
                          they have a deep awareness for the ethical and
                          philosophical considerations for the topic and
                          context.
                        </p>
                        <span className="text-style-subtitle1 mt-md">
                          Pioneer
                        </span>
                        <p className="text-style-body2">
                          The pioneer level describes one with rich and varied
                          knowledge of the system of a topic including the
                          foundational principles, relations with other topics,
                          and possible future developments. They have the skills
                          to expand and create new knowledge within topic area.
                          They have the ability to provide wise and
                          well-reasoned approaches to addressing issues in a
                          topic. They have broad and deeply-considered
                          perspectives for challenges and issues in a topic.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Disclosure>
            </HeadlessuiDialog.Panel>
          </div>
        </div>
      </div>
    </HeadlessuiDialog>
  );
}

function CompetenceHeaderAndContent(props: {
  competenceAndLevel: CompetenceAndLevel;
  competenceRelationshipChain: CompetenceBaseEx[];
  index: number;
}) {
  const { competenceAndLevel, competenceRelationshipChain, index } = props;
  const currentCompetence = competenceRelationshipChain[index];
  const isLast = index === competenceRelationshipChain.length - 1;
  const isFirst = index === 0;
  return (
    <div>
      <div className={"flex items-center gap-x-sm"}>
        <span
          className={"w-xs h-xs rounded-full bg-light-yellow flex-shrink-0"}
        />
        {isFirst ? (
          <Link
            href={buildCompetenceUrl(
              currentCompetence.id,
              currentCompetence.name
            )}
            className={mcn("text-style-body1", "link1")}
          >
            {currentCompetence.name}
          </Link>
        ) : (
          <div className={"flex items-center gap-md"}>
            <span className={mcn("text-style-body1")}>
              {currentCompetence.name}
            </span>
            {isLast && (
              <LevelTab
                level={competenceAndLevel.level ?? Level.LevelAExplorer}
              />
            )}
          </div>
        )}
      </div>
      <div className="ml-0 tablet:ml-lg mt-sm">
        {!isLast ? (
          <CompetenceHeaderAndContent
            competenceAndLevel={competenceAndLevel}
            competenceRelationshipChain={competenceRelationshipChain}
            index={index + 1}
          />
        ) : (
          <CompetenceDescriptionAndSkills
            competenceAndLevel={competenceAndLevel}
            competenceRelationshipChain={competenceRelationshipChain}
          />
        )}
      </div>
    </div>
  );
}

function CompetenceDescriptionAndSkills(props: {
  competenceAndLevel: CompetenceAndLevel;
  competenceRelationshipChain: CompetenceBaseEx[];
}) {
  const { competenceAndLevel } = props;
  const skills = competenceAndLevel.skills ?? [];
  const uniqueSkills = skills.filter(
    (skill, index) => skills.findIndex((s) => s.id === skill.id) === index
  );
  return (
    <div className="flex flex-col">
      <span className="text-style-caption mt-md">ESCO SKILLS</span>
      <div className={"flex flex-col gap-y-sm mt-2xs"}>
        {uniqueSkills.map((skill, index) => (
          <SkillTab key={index} skill={skill} />
        ))}
      </div>
    </div>
  );
}
